// 只保存页面选择；身份与设备权限始终由当前会话和接口数据决定。
export const DASHBOARD_HISTORY_KEY = 'microquakeDashboard'
const MODES = ['outline', 'outline3d', 'satellite']
const text = value => typeof value === 'string' || typeof value === 'number' ? String(value) : ''

export function dashboardOwner(user) {
  return user?.id == null ? '' : String(user.id)
}

export function dashboardSnapshot(owner, selection = {}) {
  return {
    owner: text(owner),
    province: text(selection.province) || '全国',
    projectId: text(selection.projectId),
    station: text(selection.station),
    mapMode: MODES.includes(selection.mapMode) ? selection.mapMode : 'outline'
  }
}

export function legacyDashboardState(query, owner) {
  if (!['province', 'project', 'station'].some(key => Object.hasOwn(query, key))) return null
  return {
    ...dashboardSnapshot(owner, { province: query.province, station: query.station }),
    legacyProjectName: text(query.project)
  }
}

export function cleanDashboardQuery(query) {
  const clean = { ...query }
  for (const key of ['province', 'project', 'station']) delete clean[key]
  return clean
}

export function saveDashboardHistory(history, snapshot) {
  if (JSON.stringify(history.state?.[DASHBOARD_HISTORY_KEY]) === JSON.stringify(snapshot)) return
  // 保留 Vue Router 的 position、back、forward 和 scroll 等字段。
  history.replaceState({ ...history.state, [DASHBOARD_HISTORY_KEY]: snapshot }, '')
}

export function resolveDashboardSelection(saved, owner, projects, stations) {
  const empty = { province: '全国', project: null, station: null, mapMode: 'outline' }
  if (!owner || !saved || saved.owner !== owner) return empty
  const state = dashboardSnapshot(owner, saved)
  const validStations = stations.filter(station => projects.some(project => text(project.id) === text(station.project_id)))
  const station = validStations.find(item => text(item.sid_no) === state.station)
  // 设备归属可能已经变化：优先用最新数据，绝不恢复旧名称或旧省份。
  if (station) {
    const project = projects.find(item => text(item.id) === text(station.project_id))
    return { province: station.province || '全国', project, station, mapMode: state.mapMode }
  }
  if (state.station) return { ...empty, mapMode: state.mapMode }
  const legacyMatches = projects.filter(item => item.name === saved.legacyProjectName)
  const project = projects.find(item => text(item.id) === state.projectId) ||
    (!state.projectId && legacyMatches.length === 1 ? legacyMatches[0] : null)
  if (project) {
    const projectStations = validStations.filter(item => text(item.project_id) === text(project.id))
    const counts = projectStations.reduce((all, item) => {
      if (item.province && item.province !== '全国') all[item.province] = (all[item.province] || 0) + 1
      return all
    }, {})
    const province = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || '全国'
    return { province, project, station: null, mapMode: state.mapMode }
  }
  if (state.projectId || saved.legacyProjectName) return { ...empty, mapMode: state.mapMode }
  return {
    ...empty,
    province: validStations.some(item => item.province === state.province) ? state.province : '全国',
    mapMode: state.mapMode
  }
}

export function monitorReturnLocation(historyState, owner, sid) {
  const saved = historyState?.[DASHBOARD_HISTORY_KEY]
  const snapshot = dashboardSnapshot(owner, saved?.owner === owner ? saved : {})
  // 直接访问详情也能返回并选中设备；首页拿到最新可访问列表后再确认它。
  snapshot.station = text(sid)
  return { name: 'Dashboard', state: { [DASHBOARD_HISTORY_KEY]: snapshot } }
}

export function projectForStation(station, projects) {
  if (!station) return null
  return projects.find(project => text(project.id) === text(station.project_id)) || null
}

export function stationFromMapSelection(selection, stations) {
  const sid = text(selection?.sid)
  if (sid) return stations.find(station => text(station.sid_no) === sid) || null
  const matches = stations.filter(station => station.name === selection?.name)
  return matches.length === 1 ? matches[0] : null
}

export function dashboardGridLocation(projectId) {
  const id = text(projectId)
  return id ? { name: 'DashboardGrid', query: { project: id } } : { name: 'DashboardGrid' }
}

export function dashboardStationPayload(station, longitude, latitude) {
  return { sid: text(station?.sid_no), name: station?.name || '', gps: [longitude, latitude] }
}

export function buildDashboardProjectClusters(stations = []) {
  const groups = new Map()
  const ranks = { normal: 0, yellow: 1, orange: 2, red: 3 }
  for (const station of stations) {
    const id = text(station.project_id)
    if (!id || !Array.isArray(station.value)) continue
    if (!groups.has(id)) groups.set(id, {
      projectId: station.project_id,
      projectName: station.project_name || '未知项目',
      stations: [], lng: 0, lat: 0, status: 'normal',
      eventCount: station.microseismic_event_count_24h
    })
    const group = groups.get(id)
    group.stations.push(station)
    group.lng += Number(station.value[0]) || 0
    group.lat += Number(station.value[1]) || 0
    if ((ranks[station.status] || 0) > (ranks[group.status] || 0)) group.status = station.status
  }
  return [...groups.values()].map(group => ({
    name: `${group.projectName} (${group.stations.length}个设备 · 近24小时微震 ${group.eventCount ?? '—'} 个)`,
    value: [group.lng / group.stations.length, group.lat / group.stations.length, 50],
    status: group.status,
    isCluster: true,
    projectId: group.projectId,
    projectName: group.projectName,
    eventCount: group.eventCount,
    children: group.stations
  }))
}
